<template>
    <div data-testid="dojo-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <form class="form-h"
                  @submit.prevent="submit">
                <div v-if="saveError"
                     class="alert alert-danger">
                    <p>{{ saveError }}</p>
                </div>

                <div v-if="!deviceDojoConfig.isDojoWebPos"
                     data-testid="dojo-terminal-settings-block">
                    <div class="form-group">
                        <label for="dojo-terminal-id"
                               class="control-label">{{ l('DOJO_SETTINGS.TERMINALS_TID') }}</label>
                        <div class="form-h__right">
                            <div class="input-group-flex">
                                <input v-model="deviceDojoConfig.terminalId"
                                       data-testid="dojo-terminal-id"
                                       type="text"
                                       disabled="disabled"
                                       class="form-control input-lg">
                                <button class="btn btn-info"
                                        @click.prevent="showTerminals()">
                                    {{ l('DOJO_SETTINGS.VIEW_TERMINALS') }}
                                </button>
                            </div>

                            <p class="help-block">
                                {{ l('DOJO_SETTINGS.TERMINALS_HELP_UID') }}
                            </p>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="dojo-terminal-host"
                               class="control-label">
                            {{ l('DOJO_SETTINGS.HOST') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input v-model="deviceDojoConfig.terminalHost"
                                   data-testid="dojo-terminal-host"
                                   type="text"
                                   class="form-control input-lg">
                            <PaymentConfigDefaultValue config-key="terminalHost"
                                                       :device-config="deviceDojoConfig"
                                                       :company-config="companyDojoConfig" />
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="dojo-api-key"
                               class="control-label">
                            {{ l('DOJO_SETTINGS.API_KEY') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input v-model="deviceDojoConfig.apiKey"
                                   data-testid="dojo-api-key"
                                   type="text"
                                   class="form-control input-lg">
                            <PaymentConfigDefaultValue config-key="apiKey"
                                                       :show-value="false"
                                                       :device-config="deviceDojoConfig"
                                                       :company-config="companyDojoConfig" />
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="dojo-installer-id"
                               class="control-label">
                            {{ l('DOJO_SETTINGS.INSTALLER_ID') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input v-model="deviceDojoConfig.installerId"
                                   data-testid="dojo-installer-id"
                                   type="text"
                                   class="form-control input-lg">
                            <PaymentConfigDefaultValue config-key="installerId"
                                                       :device-config="deviceDojoConfig"
                                                       :company-config="dojoConfig" />
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="dojo-software-house-id"
                               class="control-label">
                            {{ l('DOJO_SETTINGS.SOFTWARE_HOUSE_ID') }}
                        </label>
                        <div class="form-h__right fixedwidth">
                            <input v-model="dojoConfig.softwareHouseId"
                                   data-testid="dojo-software-house-id"
                                   type="text"
                                   disabled="disabled"
                                   class="form-control input-lg">
                            <p class="help-block">
                                {{ l('DOJO_SETTINGS.COMPANY_WIDE_SETTING') }}
                            </p>
                        </div>
                    </div>

                    <hr />

                    <div class="form-group">
                        <label class="control-label">
                            {{ l('DOJO_SETTINGS.SUPPORTS_REPORTS') }}
                        </label>
                        <div class="form-h__right">
                            <div class="btn-group">
                                <button data-testid="dojo-supports-reports-false"
                                        type="button"
                                        :class="{'active': !deviceDojoConfig.supportsReports}"
                                        class="btn btn-sm btn-default"
                                        @click.prevent="setSupportsReports(false)">
                                    {{ l('DOJO_SETTINGS.NO_BTN') }}
                                </button>
                                <button data-testid="dojo-supports-reports-true"
                                        type="button"
                                        :class="{'active': deviceDojoConfig.supportsReports}"
                                        class="btn btn-sm btn-default"
                                        @click.prevent="setSupportsReports(true)">
                                    {{ l('DOJO_SETTINGS.YES_BTN') }}
                                </button>
                            </div>

                            <p class="help-block">
                                {{ l('DOJO_SETTINGS.REPORTS_NO_SUPPORTED') }}
                            </p>
                        </div>
                    </div>

                    <hr />
                </div>

                <div class="form-group">
                    <label class="control-label">
                        {{ l('DOJO_SETTINGS.IS_DOJO_WEBPOS') }}
                    </label>
                    <div class="form-h__right fixedwidth">
                        <div class="btn-group">
                            <button data-testid="dojo-is-web-pos-false"
                                    type="button"
                                    :class="{'active': !deviceDojoConfig.isDojoWebPos}"
                                    class="btn btn-sm btn-default"
                                    @click.prevent="setIsDojoWebPos(false)">
                                {{ l('DOJO_SETTINGS.NO_BTN') }}
                            </button>
                            <button data-testid="dojo-is-web-pos-true"
                                    type="button"
                                    :class="{'active': deviceDojoConfig.isDojoWebPos}"
                                    class="btn btn-sm btn-default"
                                    @click.prevent="setIsDojoWebPos(true)">
                                {{ l('DOJO_SETTINGS.YES_BTN') }}
                            </button>
                            <span v-if="deviceDojoConfig.isDojoWebPos && !isAndroid"
                                  class="ml-1 text-danger">
                                <i class="fa-solid fa-exclamation-circle"></i>
                                {{ l('DOJO_SETTINGS.IS_DOJO_WEBPOS_INVALID') }}
                            </span>
                        </div>

                        <p class="help-block">
                            {{ l('DOJO_SETTINGS.IS_DOJO_WEBPOS_INFO') }}
                        </p>

                        <div class="mt-1">
                            <button v-if="dojoWebPOSExists && deviceDojoConfig.isDojoWebPos"
                                    class="btn btn-sm btn-default"
                                    @click.prevent="openWebPOS()">
                                {{ l('DOJO_SETTINGS.DOJO_WEBPOS_SETTINGS') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button data-testid="dojo-save-button"
                    class="btn btn-lg btn-primary"
                    :disabled="saving"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-solid fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import DojoConfig from '@/lib/PaymentMethods/Dojo/DojoConfig';
import PaymentConfigDefaultValue from '@/epos/vue/modals/Settings/components/PaymentConfigDefaultValue';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import { apiResponseToError } from '@/lib/Api';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { removeEmptyValues } from '@/functions/objects';

/**
 * This uses some non-translated strings because it's really for internal use only.
 */
export default {
    name: 'DojoSettingsTab',

    components: { PaymentConfigDefaultValue },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            saving: false,
            saveError: null,

            companyDojoConfig: null,

            // **This is the device config only**.
            // Other settings might exist on the company payment method.
            // If a value exists in the company config and is omitted from the device config,
            // the company level setting will be used.
            deviceDojoConfig: {
                terminalId: null,
                terminalHost: null,
                installerId: null,
                apiKey: null,
                supportsReports: false,
                isDojoWebPos: false,
            },
            device: null,
            dojoConfig: null,

            paymentMethodRef: PaymentMethodRef.CARD_DOJO_CHIP_AND_PIN,

            isAndroid: false,
            // Does the Dojo WebPOS exist on the window? It gets injected when ProPoint is loading inside
            // the Dojo WebPOS app or when ProPoint is running is development.
            dojoWebPOSExists: false,
        };
    },

    created() {
        // Load the payment method.
        const paymentMethod = container.paymentMethodService.getByRef(this.paymentMethodRef);
        if (!paymentMethod) {
            // Throw an error if the payment method is not enabled (how did we get here though?)
            throw new Error(
                container.langService.getText('DOJO_SETTINGS.ERROR_DOJO_DISABLED')
            );
        }

        // Set the existing company-level config in the Vue data.
        // This is for reference in the UI only and will not be modified here.
        this.companyDojoConfig = paymentMethod.config ? JSON.parse(paymentMethod.config) : {};
        // Clean the company config in case empty keys have been set in Backoffice.
        removeEmptyValues(this.companyDojoConfig);

        /**
         * @type {!Device}
         */
        this.device = container.currentDevice.device;

        // Copy any existing values from the device's paymentTerminalConfig.dojo object
        // to the new config (this.deviceDojoConfig).
        if (this.device.paymentTerminalConfig?.[this.paymentMethodRef]) {
            Object.entries(this.device.paymentTerminalConfig[this.paymentMethodRef])
                .forEach(([key, value]) => {
                    if (value || value === false) {
                        this.deviceDojoConfig[key] = value;
                    }
                });
        }

        this.dojoConfig = new DojoConfig();

        if (navigator.userAgent.match(/android/i)) {
            this.isAndroid = true;
        }

        // Attach the WebPOS simulator if required.
        if (container.appConfig.isDev && !window.WebPOS) {
            require('@/lib/PaymentMethods/Dojo/DojoWebPosSimulator');
        }

        this.dojoWebPOSExists = !(typeof WebPOS === 'undefined');
    },

    methods: {
        async submit() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.saveError = null;

            // Build a new config object containing just the device config we want to save.
            const newDeviceConfig = {};
            // Build a new combined company + device config object from the form data so that we can validate it.
            const newCombinedConfig = {};
            Object.keys(this.companyDojoConfig).forEach((key) => {
                if (this.companyDojoConfig[key] || this.companyDojoConfig[key] === false) {
                    newCombinedConfig[key] = this.companyDojoConfig[key];
                }
            });

            Object.keys(this.deviceDojoConfig).forEach((key) => {
                if (this.deviceDojoConfig[key] || this.deviceDojoConfig[key] === false) {
                    newCombinedConfig[key] = this.deviceDojoConfig[key];
                    newDeviceConfig[key] = this.deviceDojoConfig[key];
                }
            });

            // Clean the combined values (e.g. no keys that are just spaces).
            removeEmptyValues(newCombinedConfig);
            console.log('Validating new Dojo config.', newCombinedConfig);

            const configInstance = new DojoConfig();

            // Validate the new config.
            try {
                container.hydrator.hydrate(newCombinedConfig, configInstance);
                configInstance.validate(
                    false,
                    false, // Don't validate the username now because it is generated, not supplied in the config.
                );
            } catch (e) {
                this.saveError = e.message;
                this.saving = false;

                return;
            }

            // We always need a terminalID except for Dojo WebPOS
            if (!configInstance.terminalId && !configInstance.isDojoWebPos) {
                this.saveError = container.langService.getText('DOJO_SETTINGS.SELECT_TERMINAL');
                this.saving = false;
                return;
            }

            // Send the new settings to the API to be saved on the device.
            // Remember, we only want to update the Dojo settings not anything else that is stored in the config,
            // so the new value starts as the existing value, and we only replace one key.
            let newDevicePaymentTerminalConfig = this.device.paymentTerminalConfig;

            // In case there is no existing JSON in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig) {
                newDevicePaymentTerminalConfig = {};
            }
            // In case there is no existing Dojo key in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig.hasOwnProperty(this.paymentMethodRef)) {
                newDevicePaymentTerminalConfig[this.paymentMethodRef] = {};
            }
            newDevicePaymentTerminalConfig[this.paymentMethodRef] = newDeviceConfig;

            const newDevicePaymentTerminalConfigJson = JSON.stringify(newDevicePaymentTerminalConfig);

            container.deviceResource.setPaymentTerminalConfig(
                {
                    deviceID: this.device.ID,
                },
                {
                    paymentTerminalConfig: newDevicePaymentTerminalConfigJson,
                },
                () => {
                    // Set the new values on the current device so they are populated if the settings modal is reopened,
                    // without having to reload the app.
                    /**
                     * @type {CurrentDevice}
                     */
                    const currentDevice = container.currentDevice;
                    // Convert it back from JSON instead of using the object so that it is a clone and doesn't
                    // contain anything like Vue's reactivity.
                    currentDevice.device.paymentTerminalConfig = JSON.parse(newDevicePaymentTerminalConfigJson);

                    // Save the device to storage.
                    currentDevice.cache();

                    // Update the config in the driver, so we can make an API request to set terminalID if necessary.
                    configInstance.username = String(currentDevice.ID);
                    container.dojoDriver.config = configInstance;

                    this.saving = false;

                    // Because we've already updated the config in the driver we don't need to tell the user to sync.
                    container.toastFactory.success(
                        'DOJO_SETTINGS.SUCCESS_TOAST_TITLE'
                    );

                    this.$emit('resolve');
                },
                (result) => {
                    this.saveError = apiResponseToError(result).message;
                    this.saving = false;
                }
            );
        },

        async showTerminals() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.DOJO_TERMINALS,
                {
                    terminalId: this.deviceDojoConfig.terminalId || null,
                }
            ).then((terminalId) => {
                if (terminalId) {
                    // Clear existing errors.
                    this.saveError = null;
                    this.deviceDojoConfig.terminalId = terminalId;
                } else {
                    this.saveError = null;
                    this.deviceDojoConfig.terminalId = null;
                }
            }).catch(() => {
                // The user cancelled
            });
        },

        setIsDojoWebPos(value) {
            this.deviceDojoConfig.isDojoWebPos = value;
        },

        setSupportsReports(value) {
            this.deviceDojoConfig.supportsReports = value;
        },

        openWebPOS() {
            // eslint-disable-next-line no-undef
            WebPOS.SHOW_MENU();
        },
    },
};
</script>

<style scoped lang="less">
@import "../../../../less/variables";
.form-h__right {
    flex-wrap: wrap;
}
@media (max-width: @mobile-max) {
    .help-block {
        flex-basis: 100%;
    }
}
</style>
